import { createContext, useState, useContext, useEffect, useCallback } from 'react';
import * as Types from '../types';
import axios from 'axios';

interface UserDataContextType {
    userData: any;
    isLoading: boolean;
    cartData: Types.CartData[];
    favoriteData: Types.FavoriteData[];
    refreshUserData: () => Promise<void>;
    refreshCartData: () => Promise<void>;
    refreshFavoriteData: () => Promise<void>;
    logout: () => void;
}

const UserDataContext = createContext<UserDataContextType | null>(null);

export const UserDataProvider = ({ children }: any) => {
    const [token, setToken] = useState(localStorage.getItem('authToken'));
    const [userData, setUserData] = useState([]);
    const [cartData, setCartData] = useState([]);
    const [favoriteData, setFavoriteData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const fetchUserData = useCallback(async () => {
        if (!token) return;

        try {
          const response = await axios.get(`${import.meta.env.VITE_API_URL}/user`, {
              headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + token,
              },
          });
          setUserData(response.data);
        } catch (error) {
            console.error('Error fetching user data:', error);
            // localStorage.removeItem('authToken');
            // setToken(null);
            setUserData([]);
        }
    }, [token]);

    const fetchCartData = useCallback(async () => {
        if (!token) { 
            const existingCart = JSON.parse(localStorage.getItem('cart') || '[]');
            setCartData(existingCart);
            return;
        };

        try {
            const response = await axios.get(`${import.meta.env.VITE_API_URL}/user/cart`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + token,
                },
            });
            setCartData(response.data);
        } catch (error) {
            console.error('Error fetching cart data:', error);
            setCartData([]);
        }
    }, [token]);

    const fetchFavoriteData = useCallback(async () => {
        if (!token) { 
            const existingFavorite = JSON.parse(localStorage.getItem('favorite') || '[]');
            setFavoriteData(existingFavorite);
            return;
        };

        try {
            const response = await axios.get(`${import.meta.env.VITE_API_URL}/user/favorites`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + token,
                },
            });
            setFavoriteData(response.data);
        } catch (error) {
            console.error('Error fetching favorite data:', error);
            setFavoriteData([]);
        }
    }, [token]);

    const fetchAllData = useCallback(async () => {
        setIsLoading(true);
        await Promise.all([fetchUserData(), fetchCartData(), fetchFavoriteData()]);
        setIsLoading(false);
    }, [fetchUserData, fetchCartData, fetchFavoriteData]);

    useEffect(() => {
        if (token) fetchAllData();
    }, [fetchAllData, token]);

    // Автоматически обновлять данные пользователя каждые 60 секунд
    useEffect(() => {
        if (token) {
            const interval = setInterval(fetchUserData, 60000);
            return () => clearInterval(interval);
        }
    }, [token, fetchUserData]);

    const logout = useCallback(() => {
        localStorage.removeItem('authToken');
        setToken(null);
        setUserData([]);
        setCartData([]);
        setFavoriteData([]);
        window.location.replace("/webapp");
    }, []);

    return (
        <UserDataContext.Provider
            value={{
              userData: userData,
              isLoading: isLoading,
              cartData: cartData,
              refreshUserData: fetchUserData,
              refreshCartData: fetchCartData,
              favoriteData: favoriteData,
              refreshFavoriteData: fetchFavoriteData,
              logout,
            }}
        >
            {children}
        </UserDataContext.Provider>
    );
};

export default function useUserData() {
    const context = useContext(UserDataContext);
    if (!context) {
        throw new Error('useUserData must be used within a UserDataProvider');
    }
    return context;
}